import {KeyboardEvent, MouseEvent, useState} from 'react';

import {clamp, isNil} from 'ramda';

import {BUSINESS_HOURS_COUNT} from '../constants/TEST_DRIVE_CALENDAR';
import {getSlotFromPointer, TestDriveCalendarSlot} from '../utils/getSlotFromPointer';

interface UseSlotHoverArgs {
  firstDay: Date;
  rangeDays: number;
  isHourSubdivided: boolean;
}

const COLUMN_STEP_BY_KEY: Partial<Record<string, number>> = {ArrowLeft: -1, ArrowRight: 1};

export const useSlotHover = ({firstDay, rangeDays, isHourSubdivided}: UseSlotHoverArgs) => {
  const [hoveredSlot, setHoveredSlot] = useState<{
    vehicleId: string;
    slot: TestDriveCalendarSlot;
  } | null>(null);

  const readSlot = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();

    return getSlotFromPointer({
      pointerX: event.clientX,
      rowLeft: bounds.left,
      rowWidth: bounds.width,
      windowStart: firstDay,
      rangeDays,
    });
  };

  const columnCount = isHourSubdivided ? rangeDays * BUSINESS_HOURS_COUNT : rangeDays;

  const getSlotAtColumn = (columnIndex: number) =>
    getSlotFromPointer({
      pointerX: (columnIndex + 0.5) / columnCount,
      rowLeft: 0,
      rowWidth: 1,
      windowStart: firstDay,
      rangeDays,
    });

  const handleSlotKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    vehicleId: string,
    slot: TestDriveCalendarSlot | null
  ) => {
    const step = COLUMN_STEP_BY_KEY[event.key];

    if (isNil(step) || isNil(slot)) {
      return;
    }

    event.preventDefault();
    const columnIndex = clamp(0, columnCount - 1, slot.columnIndex + step);
    setHoveredSlot({vehicleId, slot: getSlotAtColumn(columnIndex)});
  };

  return {
    hoveredSlot,
    setHoveredSlot,
    readSlot,
    columnCount,
    getSlotAtColumn,
    handleSlotKeyDown,
  };
};
